// AF-3.16 — Brand Growth Command Center. The primary destination after
// selecting a Brand entity. Sources its entire state from AF-3.14's
// BrandOutcomeControlPlaneService in a single call -- this page performs
// NO client-side scoring, no re-derivation of gap/control/brand state,
// and never fabricates a value the backend did not return.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, GovernanceBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { MetricCard } from '@/components/brand/MetricCard';
import { NextBestActionCard, AlternativeActionsList } from '@/components/brand/NextBestActionCard';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { EmptyState, ErrorState, LoadingState, InsufficientEvidenceState } from '@/components/brand/StateViews';
import { useBrandContext } from '@/hooks/useBrandContext';
import { controlPlaneAPI, bridgeSummaryAPI } from '@/lib/intelligenceApi';
import { CaretRight, Binoculars, Lightbulb } from '@phosphor-icons/react';
import { GLIMState } from '@/brand';
import { Link } from 'react-router-dom';

export default function BrandCommandCenterPage() {
  const ctx = useBrandContext();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Martian Bridge -- the reverse cross-link back to the briefing page (/):
  // the briefing page already links forward to /growth/opportunities via
  // RecommendationsAwaitingCard and GrowthOSBridgeSignal; this is the one
  // missing link the other direction. Reads the SAME real, already-
  // production-audited GET /intelligence/{workspace_id}/bridge-summary
  // route (bridgeSummaryAPI.get) -- no new backend logic. Gates on
  // ctx.workspaceId ALONE, never ctx.ready -- the same fix
  // RecommendationsAwaitingCard's own remediation required, since
  // ctx.ready additionally needs an entity to resolve and most real
  // clients have none, which would otherwise leave this permanently
  // unresolved for the vast majority of workspaces.
  const [briefingSummary, setBriefingSummary] = useState(null); // null = not loaded yet
  const [briefingFailed, setBriefingFailed] = useState(false);

  useEffect(() => {
    if (!ctx.workspaceId) return undefined;
    let cancelled = false;
    bridgeSummaryAPI.get(ctx.workspaceId)
      .then((res) => { if (!cancelled) setBriefingSummary(res.data); })
      .catch(() => { if (!cancelled) setBriefingFailed(true); });
    return () => { cancelled = true; };
  }, [ctx.workspaceId]);

  // Defensive numeric guard -- never lets a missing/null/non-numeric
  // field crash this card or render a fabricated count (same discipline
  // as growthOsBridgeSignal.js's normalizeBridgeSummary()). "Awaiting
  // review" mirrors RecommendationsAwaitingCard's own awaitingCount
  // definition (ACTIVE + ACKNOWLEDGED -- no final human decision yet),
  // so the count shown here matches what the user will see when they
  // click through.
  const toCount = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);
  const briefingAwaitingCount = briefingSummary
    ? toCount(briefingSummary.recommendations?.awaiting_decision) + toCount(briefingSummary.recommendations?.acknowledged)
    : 0;

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    controlPlaneAPI.create(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setState(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    if (!state) return;
    setLoading(true);
    controlPlaneAPI.refresh(ctx.workspaceId, { state_id: state.id })
      .then((r) => setState(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  };

  const objectiveResolved = state && state.primary_objective && state.primary_objective !== 'UNKNOWN';

  return (
    <DashboardLayout>
      <div data-testid="brand-command-center-page" className="max-w-5xl mx-auto">
        <div className="mb-5">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Brand Growth Command Center</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">What is happening, why Martian believes it, and what should happen next.</p>
        </div>

        <BrandContextBar ctx={ctx} />

        {/* Martian Bridge — the reverse cross-link back to the briefing page
            (/): the briefing page already links forward to this control
            plane via RecommendationsAwaitingCard and GrowthOSBridgeSignal;
            this is the one missing link the other direction, completing
            the original bridge spec. Self-contained and silent on
            failure, matching RecommendationsAwaitingCard's own
            discipline: renders nothing while loading, nothing on
            failure, and nothing when there is genuinely nothing awaiting
            review — never a fabricated count, never an error box. */}
        {!briefingFailed && briefingAwaitingCount > 0 && (
          <Link
            to="/"
            data-testid="briefing-link-card"
            className="mb-5 flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-card)] hover:border-[var(--accent-border)] hover:shadow-[var(--shadow-hover)] transition-all"
          >
            <div className="w-9 h-9 flex items-center justify-center shrink-0">
              <GLIMState state="recommending" size={36} showLabel={false} motion={false} />
            </div>
            <div className="flex-1">
              <p className="text-[12px] text-[var(--text-primary)] font-semibold">Martian's morning briefing</p>
              <p className="text-[10px] text-[var(--text-tertiary)]">
                Martian has prepared {briefingAwaitingCount} recommendation{briefingAwaitingCount !== 1 ? 's' : ''} awaiting your review
              </p>
            </div>
            <CaretRight size={14} className="text-[var(--text-tertiary)]" />
          </Link>
        )}

        {/* AF-3.19 — the one cross-link the AF-3.19 audit itself called out
            as missing: Overview never pointed at "how is AI discovering
            you". This card adds only a link, no new computation. */}
        <Link
          to="/ai-discovery"
          data-testid="ai-discovery-cross-link"
          className="mb-5 flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-card)] hover:border-[var(--accent-border)] hover:shadow-[var(--shadow-hover)] transition-all"
        >
          <div className="w-9 h-9 flex items-center justify-center shrink-0">
            <Binoculars size={16} className="text-[var(--accent)]" />
          </div>
          <div className="flex-1">
            <p className="text-[12px] text-[var(--text-primary)] font-semibold">How is AI discovering this brand?</p>
            <p className="text-[10px] text-[var(--text-tertiary)]">Share of Model, AI answers &amp; mentions, citations — in AI Discovery</p>
          </div>
          <CaretRight size={14} className="text-[var(--text-tertiary)]" />
        </Link>

        {!ctx.ready && !ctx.loadingClients && (
          <EmptyState title="Select a workspace and entity" description="Choose a brand workspace and entity above to view its growth state." />
        )}

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && state && (
          <>
            {/* PAGE CONTEXT — what is happening */}
            <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-1.5">Context</p>
            <div data-testid="command-center-header" className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 mb-5 shadow-[var(--shadow-card)] flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">{ctx.clients.find((c) => c.id === ctx.workspaceId)?.name || ctx.workspaceId}</p>
                <p className="text-[14px] text-[var(--text-primary)] font-semibold">{ctx.selectedEntity?.canonical_name || ctx.entityId}</p>
              </div>
              <div className="flex items-center gap-5">
                <div>
                  <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Brand state</p>
                  <button data-testid="brand-state-trigger" onClick={() => setDrawerOpen(true)} className="mt-0.5">
                    <StatusBadge value={state.status} />
                  </button>
                </div>
                <div>
                  <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p>
                  <ConfidenceIndicator value={state.confidence} />
                </div>
                <div>
                  <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Last refresh</p>
                  <p className="text-[10px] font-mono text-[var(--text-secondary)]" data-testid="last-refresh-timestamp">
                    {state.updated_at ? new Date(state.updated_at).toLocaleString() : 'Not available'}
                  </p>
                </div>
              </div>
            </div>

            {!objectiveResolved ? (
              <InsufficientEvidenceState entityLabel={ctx.selectedEntity?.canonical_name} testId="objective-not-resolved" />
            ) : (
              <>
                {/* KEY SIGNAL — what changed */}
                <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-1.5">Key signal</p>
                <div data-testid="primary-objective-card" className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-5 mb-5 shadow-[var(--shadow-card)]">
                  <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Primary Objective</p>
                  <p className="font-display text-[22px] font-bold text-[var(--text-primary)] mb-3" data-testid="primary-objective-value">{state.primary_objective}</p>
                  <div className="flex items-center gap-6 flex-wrap text-[11px]">
                    <div>
                      <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Primary metric</p>
                      <p className="text-[var(--text-primary)] font-mono">{state.primary_metric}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Guardrails</p>
                      <p className="text-[var(--text-primary)] font-mono">{(state.guardrail_metrics || []).join(', ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Source</p>
                      <p className="text-[var(--text-secondary)]">{state.objective_source}</p>
                    </div>
                  </div>
                </div>

                {/* EVIDENCE — why we believe this (metric performance) */}
                <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-1.5">Evidence</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5" data-testid="performance-strip">
                  <MetricCard
                    label={state.primary_metric}
                    baseline={state.baseline}
                    current={state.current_performance}
                    status={state.outcome_summary?.whether_conclusive === false ? 'INCONCLUSIVE' : state.control_status}
                  />
                  {(state.guardrail_metrics || []).map((g) => {
                    const breached = (state.outcome_summary?.which_guardrails_moved?.breached || []).includes(g);
                    const clear = (state.outcome_summary?.which_guardrails_moved?.clear || []).includes(g);
                    return (
                      <MetricCard
                        key={g}
                        label={g}
                        isGuardrail
                        baseline={{ baseline_status: 'UNKNOWN' }}
                        current={{ current_value: breached ? 'Breached' : clear ? 'Clear' : null, delta_pct: null }}
                        status={breached ? 'BREACHED' : clear ? 'CLEAR' : undefined}
                      />
                    );
                  })}
                </div>

                {/* NEXT BEST ACTION — what should I do next (visually dominant) */}
                <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-1.5">Next best action</p>
                <NextBestActionCard
                  action={state.next_best_action}
                  reason={state.primary_control_decision?.reason}
                  readiness={state.primary_control_decision?.readiness}
                  blockers={state.primary_control_decision?.blockers}
                  onViewEvidence={() => setDrawerOpen(true)}
                />
                <AlternativeActionsList alternatives={state.alternative_actions} />

                {/* IMPLICATION — governance / environment risk */}
                {state.environment_risks?.length > 0 && (
                  <div className="mt-5 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="governance-strip">
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Environment risks</p>
                    <div className="flex flex-wrap gap-2">
                      {state.environment_risks.map((r, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-[10px]">
                          <span className="text-[var(--text-secondary)]">{r.object_type}</span>
                          <GovernanceBadge value={r.state} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button data-testid="view-full-evidence-link" onClick={() => setDrawerOpen(true)} className="mt-4 flex items-center gap-1 text-[11px] text-[var(--accent)] font-medium">
                  <Lightbulb size={13} /> Why does Martian believe this? <CaretRight size={12} />
                </button>
              </>
            )}
          </>
        )}

        {state && (
          <EvidenceDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            title="Why Martian assigned this state"
            evidence={state.evidence}
            provenance={state.provenance}
            unknowns={state.narrative?.what_we_do_not_know}
            causality={state.causality}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
